import React, { useEffect, useState } from "react";
import { apiGet } from "../../services/api";
import {
    DaySummaryResponse,
    FinancePanelKey,
    PharmacyCollectionResponse,
    SupplierOption,
} from "./types";
import ConsultantPayouts from "./ConsultantPayouts";
import ClinicVendorBills from "./ClinicVendorBills";
import PharmacyVendorBills from "./PharmacyVendorBills";

interface FinancePanelProps {
    consultantServiceFees: any[];
    consultantServices: any[];
    showToast: (args: { message: string; type: "success" | "error" | "info" | "warning" }) => void;
}

export default function FinancePanel({ consultantServiceFees, consultantServices, showToast }: FinancePanelProps) {
    const [financeDate, setFinanceDate] = useState<string>(new Date().toISOString().slice(0, 10));
    const [daySummary, setDaySummary] = useState<DaySummaryResponse | null>(null);
    const [pharmacyCollection, setPharmacyCollection] = useState<PharmacyCollectionResponse | null>(null);
    const [financePanel, setFinancePanel] = useState<FinancePanelKey>("consultant");

    const [consultants, setConsultants] = useState<{ id: number; name: string }[]>([]);
    const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
    const [pharmacySuppliers, setPharmacySuppliers] = useState<SupplierOption[]>([]);

    const loadFinanceSnapshot = async () => {
        try {
            const [ds, pc] = await Promise.all([
                apiGet(`/api/manager/day-summary?date=${financeDate}`),
                apiGet(`/api/pharmacy/collection?date=${financeDate}`),
            ]);
            setDaySummary(ds as DaySummaryResponse);
            setPharmacyCollection(pc as PharmacyCollectionResponse);
        } catch {
            setDaySummary(null);
            setPharmacyCollection(null);
        }
    };

    useEffect(() => {
        void loadFinanceSnapshot();
        apiGet("/api/doctor/consultants").then((res: any) => {
            setConsultants(Array.isArray(res) ? res : []);
        });
        apiGet("/api/manager/suppliers").then((res: any) => {
            setSuppliers(Array.isArray(res) ? res : []);
        });
        apiGet("/api/pharmacy/suppliers").then((res: any) => {
            setPharmacySuppliers(Array.isArray(res) ? res : []);
        });
    }, [financeDate]);

    const clinicCollectedAmount = Number(daySummary?.clinical_revenue?.collected ?? daySummary?.totals?.collected_total ?? 0);
    const pharmacyCollectedAmount = Number(pharmacyCollection?.total || 0);
    const clinicNetAmount = Number(daySummary?.totals?.net_cash_in_hand || 0);

    return (
        <section className="mod-standalone-window">
            <div className="mod-window-header">
                <div>
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        📊 Financial Operations
                    </h2>
                    <p className="mod-text-muted">Manage consultant payouts, vendor bills, and daily metrics.</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--mod-text-muted)' }}>Target Date:</label>
                    <input 
                        type="date" 
                        value={financeDate} 
                        onChange={(e) => setFinanceDate(e.target.value)} 
                        style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }} 
                    />
                </div>
            </div>

            {/* Daily Metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase' }}>Clinic Collection</span>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#15803d', marginTop: '4px' }}>₹{clinicCollectedAmount.toLocaleString("en-IN")}</div>
                </div>
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '12px', padding: '16px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase' }}>Pharmacy Collection</span>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#c2410c', marginTop: '4px' }}>₹{pharmacyCollectedAmount.toLocaleString("en-IN")}</div>
                </div>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase' }}>Net Cash Flow</span>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1d4ed8', marginTop: '4px' }}>₹{clinicNetAmount.toLocaleString("en-IN")}</div>
                </div>
            </div>

            {/* Navigation */}
            <div className="mod-sub-nav" style={{ marginBottom: '20px', width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: '#f1f5f9' }}>
                <button className={`mod-sub-btn ${financePanel === "consultant" ? "active" : ""}`} onClick={() => setFinancePanel("consultant")}>Consultant Pay</button>
                <button className={`mod-sub-btn ${financePanel === "clinicSupplier" ? "active" : ""}`} onClick={() => setFinancePanel("clinicSupplier")}>Clinic Vendor</button>
                <button className={`mod-sub-btn ${financePanel === "pharmacySupplier" ? "active" : ""}`} onClick={() => setFinancePanel("pharmacySupplier")}>Pharmacy Vendor</button>
            </div>

            {/* Panels */}
            {financePanel === "consultant" && (
                <ConsultantPayouts 
                    consultants={consultants} 
                    consultantServiceFees={consultantServiceFees} 
                    consultantServices={consultantServices} 
                    financeDate={financeDate} 
                    showToast={showToast} 
                    onSuccess={loadFinanceSnapshot} 
                />
            )}

            {financePanel === "clinicSupplier" && (
                <ClinicVendorBills 
                    suppliers={suppliers} 
                    financeDate={financeDate} 
                    showToast={showToast} 
                    onSuccess={loadFinanceSnapshot} 
                />
            )}

            {financePanel === "pharmacySupplier" && (
                <PharmacyVendorBills 
                    pharmacySuppliers={pharmacySuppliers} 
                    financeDate={financeDate} 
                    showToast={showToast} 
                    onSuccess={loadFinanceSnapshot} 
                />
            )}
        </section>
    );
}
